import { createContext, useContext, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { submitTrialLead, submitDemoLead } from "@/lib/api";

const ModalContext = createContext({ openTrial: () => {}, openDemo: () => {} });
export const useLeadModals = () => useContext(ModalContext);

const inputCls =
  "w-full rounded-md border-2 border-hero-black/15 bg-white px-4 py-3 text-base text-hero-black placeholder:text-hero-black/40 focus:border-hero-blue focus:outline-none focus:ring-2 focus:ring-hero-blue/30 transition-colors";

const Field = ({ label, children, htmlFor }) => (
  <div className="space-y-1.5">
    <label htmlFor={htmlFor} className="block text-sm font-semibold text-hero-black">
      {label}
    </label>
    {children}
  </div>
);

const SubmitButton = ({ loading, testId, children }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testId}
    className="w-full rounded-md bg-hero-gold px-6 py-3.5 font-display text-lg font-semibold uppercase tracking-wide text-hero-black shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
  >
    {loading && <Loader2 className="h-5 w-5 animate-spin" />}
    {children}
  </button>
);

const TrialForm = ({ onDone }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", use_case: "360° Room Tours" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitTrialLead(form);
      toast.success("You're in! We'll reach out within 24 hours to kick off your first month at 50% off.");
      onDone();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="trial-signup-form">
      <Field label="Full name" htmlFor="trial-name">
        <input id="trial-name" data-testid="trial-name-input" required className={inputCls} placeholder="Jane Cooper" value={form.name} onChange={set("name")} />
      </Field>
      <Field label="Work email" htmlFor="trial-email">
        <input id="trial-email" data-testid="trial-email-input" required type="email" className={inputCls} placeholder="jane@brokerage.com" value={form.email} onChange={set("email")} />
      </Field>
      <Field label="Company / Brokerage" htmlFor="trial-company">
        <input id="trial-company" data-testid="trial-company-input" required className={inputCls} placeholder="Cooper Realty Group" value={form.company} onChange={set("company")} />
      </Field>
      <Field label="What do you need videos for?" htmlFor="trial-usecase">
        <select id="trial-usecase" data-testid="trial-usecase-select" className={inputCls} value={form.use_case} onChange={set("use_case")}>
          <option>360° Room Tours</option>
          <option>Drone Flights</option>
          <option>Promo Clips</option>
          <option>A mix of everything</option>
        </select>
      </Field>
      <SubmitButton loading={loading} testId="trial-submit-button">Start First Month at 50% Off</SubmitButton>
      <p className="text-center text-sm text-hero-black/60">$1,000 for month 1 instead of $2,000. Cancel anytime.</p>
    </form>
  );
};

const DemoForm = ({ onDone }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", preferred_time: "Morning (9am–12pm)" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitDemoLead(form);
      toast.success("Demo request received! We'll confirm your 15-minute slot by email.");
      onDone();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="demo-booking-form">
      <Field label="Full name" htmlFor="demo-name">
        <input id="demo-name" data-testid="demo-name-input" required className={inputCls} placeholder="Jane Cooper" value={form.name} onChange={set("name")} />
      </Field>
      <Field label="Work email" htmlFor="demo-email">
        <input id="demo-email" data-testid="demo-email-input" required type="email" className={inputCls} placeholder="jane@brokerage.com" value={form.email} onChange={set("email")} />
      </Field>
      <Field label="Company / Brokerage" htmlFor="demo-company">
        <input id="demo-company" data-testid="demo-company-input" required className={inputCls} placeholder="Cooper Realty Group" value={form.company} onChange={set("company")} />
      </Field>
      <Field label="Phone" htmlFor="demo-phone">
        <input id="demo-phone" data-testid="demo-phone-input" required type="tel" className={inputCls} placeholder="+1 (512) 555-0134" value={form.phone} onChange={set("phone")} />
      </Field>
      <Field label="Preferred time" htmlFor="demo-time">
        <select id="demo-time" data-testid="demo-time-select" className={inputCls} value={form.preferred_time} onChange={set("preferred_time")}>
          <option>Morning (9am–12pm)</option>
          <option>Afternoon (12pm–4pm)</option>
          <option>Evening (4pm–7pm)</option>
        </select>
      </Field>
      <SubmitButton loading={loading} testId="demo-submit-button">Book 15-Min Demo</SubmitButton>
      <p className="text-center text-sm text-hero-black/60">15 minutes. No pressure, no obligations.</p>
    </form>
  );
};

export const LeadModalProvider = ({ children }) => {
  const [open, setOpen] = useState(null);

  return (
    <ModalContext.Provider value={{ openTrial: () => setOpen("trial"), openDemo: () => setOpen("demo") }}>
      {children}

      <Dialog open={open === "trial"} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="border-2 border-hero-black bg-white sm:rounded-md" data-testid="trial-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold uppercase tracking-tight text-hero-black">
              Start Your First Month — 50% Off
            </DialogTitle>
            <DialogDescription className="text-hero-black/60">
              $1,000 for month 1 instead of $2,000. Your first video brief can be live today.
            </DialogDescription>
          </DialogHeader>
          <TrialForm onDone={() => setOpen(null)} />
        </DialogContent>
      </Dialog>

      <Dialog open={open === "demo"} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="border-2 border-hero-black bg-white sm:rounded-md" data-testid="demo-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold uppercase tracking-tight text-hero-black">
              Book a 15-Min Demo
            </DialogTitle>
            <DialogDescription className="text-hero-black/60">
              See real samples and how the workflow fits your team.
            </DialogDescription>
          </DialogHeader>
          <DemoForm onDone={() => setOpen(null)} />
        </DialogContent>
      </Dialog>
    </ModalContext.Provider>
  );
};
